import { useState, useRef, useCallback, useEffect } from 'react';

const MIN_SIDEBAR_W = 350;
const MAX_SIDEBAR_W = 1000;

interface UseSidebarResizeOptions {
    wrapperRef: React.RefObject<HTMLDivElement | null>;
    setContainerWidth: React.Dispatch<React.SetStateAction<number>>;
    /** Стартовое состояние сайдбара замечаний (по умолчанию развёрнут). */
    initialSidebarCollapsed?: boolean;
}

export function useSidebarResize({
    wrapperRef,
    setContainerWidth,
    initialSidebarCollapsed = false,
}: UseSidebarResizeOptions) {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(initialSidebarCollapsed);
    const [sidebarWidth, setSidebarWidth] = useState(MIN_SIDEBAR_W);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const dividerDraggingRef = useRef(false);
    const dividerDragRef = useRef<{ startX: number; startWidth: number } | null>(null);

    const handleDividerDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const sb = sidebarRef.current;
        if (!sb) return;
        dividerDraggingRef.current = true;
        dividerDragRef.current = { startX: e.clientX, startWidth: sb.offsetWidth };
        sb.style.transition = 'none';
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, []);

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!dividerDraggingRef.current) return;
            const d = dividerDragRef.current;
            const sb = sidebarRef.current;
            if (!d || !sb) return;
            const w = Math.min(MAX_SIDEBAR_W, Math.max(MIN_SIDEBAR_W, d.startWidth + (d.startX - e.clientX)));
            sb.style.width = w + 'px';
        };
        const onUp = () => {
            if (!dividerDraggingRef.current) return;
            dividerDraggingRef.current = false;
            const d = dividerDragRef.current;
            const sb = sidebarRef.current;
            dividerDragRef.current = null;
            if (sb) {
                sb.style.transition = '';
                const finalWidth = sb.offsetWidth;
                setSidebarWidth(finalWidth);
                // Force containerWidth update — ResizeObserver was suppressed during drag,
                // and setSidebarWidth alone won't trigger it because the DOM width is already correct.
                const cw = wrapperRef.current?.offsetWidth;
                if (cw) setContainerWidth(cw);
            }
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [wrapperRef, setContainerWidth]);

    // Замер доступной ширины (ResizeObserver ловит и ресайз окна, и сворачивание сайдбара)
    useEffect(() => {
        const el = wrapperRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver((entries) => {
            if (dividerDraggingRef.current) return; // skip re-renders during sidebar drag
            const w = entries[0].contentRect.width;
            setContainerWidth((prev) => (Math.abs(prev - w) < 1 ? prev : w));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [wrapperRef, setContainerWidth]);

    return {
        sidebarRef,
        sidebarCollapsed,
        setSidebarCollapsed,
        sidebarWidth,
        handleDividerDown,
    };
}
